import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhoneIcon from "@mui/icons-material/Phone";
import PhoneDisabledIcon from "@mui/icons-material/PhoneDisabled";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import Box from "@mui/material/Box";
import Fab from "@mui/material/Fab";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { NON_BREAKABLE_SPACE } from "constants/common";
import useVoiceUserSettings from "hooks/use-voice-user-settings.hook";

import useSunoDialer from "../use-suno-dialer.hook";
import VoiceSettingsButton from "../voice-settings-widget/voice-settings-button.component";
import PhoneNumberTab from "./components/phone-number-tab/phone-number-tab.component";
import SearchPatientTab from "./components/search-patient-tab/search-patient-tab.component";

const animation = {
  "@keyframes GradientBackground": {
    "0%": {
      background: `linear-gradient(180deg, hsla(272, 18%, 48%, 0.85) 68%, hsla(154, 32%, 62%, 1) 100%)`
    },

    "25%": {
      background: `linear-gradient(0deg, hsla(272, 18%, 48%, 0.85) 68%, hsla(154, 32%, 62%, 19) 100%)`
    },

    "50%": {
      background: `linear-gradient(180deg, hsla(272, 18%, 48%, 0.85) 68%, hsla(154, 32%, 62%, 1) 100%)`
    },
    "75%": {
      background: `linear-gradient(0deg, hsla(272, 18%, 48%, 0.85) 68%, hsla(154, 32%, 62%, 1) 100%)`
    },
    "100%": {
      background: `linear-gradient(180deg, hsla(272, 18%, 48%, 0.85) 68%, hsla(154, 32%, 62%, 1) 100%)`
    }
  }
};

export default function OutgoingCallWidget() {
  const theme = useTheme();
  const { isExpanded, handleToggleExpand, outgoingCall } = useSunoDialer();

  const { voiceSettings } = useVoiceUserSettings();

  const dialInProgress = Boolean(outgoingCall?.dialInProgress);

  const incommingCallsAllowed = !voiceSettings.do_not_disturb;

  if (!isExpanded && !dialInProgress) {
    return (
      <Tooltip title="Make a Call" disableInteractive>
        <Fab
          size="small"
          color="secondary"
          sx={{
            background: "hsla(273, 21%, 54%, 0.85)",
            boxShadow: theme.shadows[3],
            "&:hover": {
              background: "hsla(273, 21%, 54%, 1)"
            }
          }}
          onClick={handleToggleExpand}
        >
          {incommingCallsAllowed ? <PhoneIcon /> : <PhoneDisabledIcon />}
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Box
      sx={{
        width: outgoingCall?.displayPatients
          ? "340px"
          : outgoingCall?.dialInProgress
            ? "300px"
            : "270px",

        marginLeft: "auto",
        marginRight: 0
      }}
    >
      <Stack
        sx={{
          color: "white",
          background: !isExpanded
            ? "hsla(273, 21%, 54%, 0.85)"
            : "hsla(272, 18%, 48%, 0.85)",
          px: 1.5,
          pt: 0.75,
          pb: 0.75,
          borderRadius: "8px",
          boxShadow: theme.shadows[3],
          cursor: "pointer",
          "&:hover": {
            background: !isExpanded ? "hsla(273, 21%, 54%, 1)" : undefined
          },
          animationName: outgoingCall?.dialInProgress
            ? "GradientBackground"
            : undefined,
          animationDuration: "5s",
          animationIterationCount: "infinite",
          ...animation
        }}
        onClick={handleToggleExpand}
      >
        <Stack
          direction="row"
          justifyContent="flex-between"
          width="100%"
          alignItems="center"
        >
          <Stack width="100%" height="100%" spacing={0.25} pr={2}>
            <Typography
              sx={{
                fontSize: "15px",
                lineHeight: 1.3,
                textShadow: outgoingCall?.dialInProgress
                  ? `1px 1px ${grey[700]}`
                  : undefined
              }}
            >
              {dialInProgress ? (
                <PhoneInTalkIcon
                  sx={{
                    fontSize: "16px",
                    mr: 1,
                    color: "white",
                    transform: "translateY(3px)"
                  }}
                />
              ) : (
                <PhoneIcon
                  sx={{
                    fontSize: "16px",
                    mr: 1,
                    color: "white",
                    transform: "translateY(3px)"
                  }}
                />
              )}

              {dialInProgress
                ? `Outgoing Call is in${NON_BREAKABLE_SPACE}Progress`
                : "Make a Call"}
            </Typography>
          </Stack>
          <Stack direction="row">
            {!incommingCallsAllowed && (
              <Tooltip
                title={`Incoming calls are not available (${
                  voiceSettings.do_not_disturb
                    ? "Do not disturb"
                    : "Ring in Suno Web App is disabled"
                })`}
                disableInteractive
              >
                <DoNotDisturbOnIcon
                  sx={{
                    p: 0,
                    transform: "scale(0.8)",
                    color: "white",
                    cursor: "pinter"
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                  }}
                />
              </Tooltip>
            )}

            <VoiceSettingsButton />
            <IconButton
              size="small"
              sx={{
                p: 0,
                transform: "scale(0.9)",

                color: "white"
              }}
            >
              {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Stack>
        </Stack>

        {isExpanded && (
          <Stack
            sx={{
              mt: 0.5,
              py: 0.5,
              pb: 2,
              borderRadius: "8px",
              cursor: "default"
            }}
            alignItems="center"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {outgoingCall?.displayPatients ? (
              <SearchPatientTab />
            ) : (
              <PhoneNumberTab />
            )}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
